// "Feedback" button + dialog (available on every page); sends the current page and screen size along.
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, FEEDBACK_KINDS, type FeedbackKind } from './lib/api';
import { deviceName } from './lib/device';
import { useToast } from './hooks';
import { Icon } from './ui';

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="fb-btn" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <Icon name="chat" sw={1.8} />
        <span>Feedback</span>
      </button>
      {open && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const loc = useLocation();
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const text = useRef<HTMLTextAreaElement>(null);
  const page = loc.pathname + loc.search;
  useEffect(() => {
    text.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  async function send() {
    if (body.trim().length < 3) return setError('Bitte schreib kurz, worum es geht.');
    setBusy(true);
    setError('');
    try {
      await api('/feedback', {
        method: 'POST',
        body: {
          kind,
          body: body.trim(),
          page,
          context: { viewport: `${window.innerWidth}×${window.innerHeight}`, device: deviceName(navigator.userAgent) },
        },
      });
      void qc.invalidateQueries({ queryKey: ['feedback'] });
      toast('Danke! Dein Feedback findest du im Profil.');
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  return (
    <div className="modal" role="presentation" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" role="dialog" aria-modal="true" aria-labelledby="fb-title">
        <p className="eyebrow">Sportplaner verbessern</p>
        <h2 id="fb-title" className="d-title">
          Feedback
        </h2>
        <div className="seg" role="group" aria-label="Art">
          {(Object.keys(FEEDBACK_KINDS) as FeedbackKind[]).map(k => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {FEEDBACK_KINDS[k]}
            </button>
          ))}
        </div>
        <label className="fl" style={{ marginTop: 12 }}>
          <span>{kind === 'bug' ? 'Was ist passiert?' : kind === 'idea' ? 'Deine Idee' : 'Deine Frage'}</span>
          <textarea
            ref={text}
            rows={6}
            maxLength={4000}
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder={
              kind === 'bug'
                ? 'Was hast du gemacht, was ist passiert, was hättest du erwartet?'
                : kind === 'idea'
                  ? 'Was würde dir helfen?'
                  : 'Was möchtest du wissen?'
            }
          />
        </label>
        <p className="note">
          Mitgeschickt werden die Seite (<code>{page}</code>), Bildschirmgröße und Gerät. Nur die Admins sehen dein
          Feedback; den Stand siehst du im Profil.
        </p>
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button className="btn btn-ghost" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void send()}>
            {busy ? 'Sende …' : 'Senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
